import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { JobDetail } from '@/features/jobs/job-shared/job-by-id-query';

/**
 * EU-551/EU-552 — orquestração das operações bulk contra o BFF recruiter:
 * dry-run (classificação para a etapa de Confirmação) e execução, em lotes de
 * até 500 ids (cap do contrato). Os conflitos das páginas somam com os IDs
 * malformados detectados LOCALMENTE no parse do XLSX.
 */

export const BULK_BATCH_SIZE = 500;
export const FATORH_PUSH_BATCH_SIZE = 50;

export class BulkOperationError extends Error {
  readonly name = 'BulkOperationError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

export interface BulkConflictCandidate {
  userId: string;
  name: string | null;
  email: string | null;
}

/** Classificação agregada (todas as páginas de lote somadas). */
export interface BulkClassification {
  /** Aptos (dry-run: "seriam processados"; execução: processados). */
  eligible: string[];
  cancelled: BulkConflictCandidate[];
  /** Já movimentados (advance) ou já reprovados (reject). */
  alreadyProcessed: BulkConflictCandidate[];
  /** Reprovados (só advance) — decisão terminal, não movimentam (2026-07-30). */
  rejected: BulkConflictCandidate[];
  /** MOV-INC-05 — aptos porém FORA da "Fase de origem" (só advance). */
  notInOriginStep: BulkConflictCandidate[];
  /** IDs válidos porém não inscritos nesta vaga (servidor). */
  notSubscribed: string[];
}

interface AdvanceResponse {
  advanced: string[];
  conflicts: {
    cancelled: BulkConflictCandidate[];
    alreadyInPhase: BulkConflictCandidate[];
    /** Ausente num BFF/Core ainda não atualizado (janela de deploy). */
    rejected?: BulkConflictCandidate[];
    /** Ausente num BFF/Core ainda não atualizado (janela de deploy). */
    notInOriginStep?: BulkConflictCandidate[];
    notSubscribed: string[];
  };
}

interface RejectResponse {
  rejected: string[];
  conflicts: {
    cancelled: BulkConflictCandidate[];
    alreadyRejected: BulkConflictCandidate[];
    notSubscribed: string[];
  };
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

const emptyClassification = (): BulkClassification => ({
  eligible: [],
  cancelled: [],
  alreadyProcessed: [],
  rejected: [],
  notInOriginStep: [],
  notSubscribed: [],
});

/** Movimentação (EU-551): `POST /jobs/:id/steps/:stepId/advance` em lotes. */
export async function runAdvance(input: {
  jobId: string;
  destStepId: string;
  /** MOV-INC-05 — "Fase de origem": o Core filtra a elegibilidade por ela. */
  originStepId: string | null;
  userIds: string[];
  dryRun: boolean;
}): Promise<BulkClassification> {
  const merged = emptyClassification();
  for (const batch of chunk(input.userIds, BULK_BATCH_SIZE)) {
    const res = await fetchClient<AdvanceResponse>({
      method: 'POST',
      url: `/jobs/${input.jobId}/steps/${input.destStepId}/advance`,
      data: {
        userIds: batch,
        // Movimentar carimba fases anteriores como concluídas (direção livre —
        // retroceder não fecha nada porque não há fase anterior em aberto).
        markPreviousCompleted: true,
        ...(input.originStepId ? { originStepId: input.originStepId } : {}),
        dryRun: input.dryRun,
      },
    });
    if (res.error) {
      throw new BulkOperationError(
        `Bulk advance failed (status ${res.error.status})`,
        res.error.status
      );
    }
    merged.eligible.push(...res.data.advanced);
    merged.cancelled.push(...res.data.conflicts.cancelled);
    merged.alreadyProcessed.push(...res.data.conflicts.alreadyInPhase);
    merged.rejected.push(...(res.data.conflicts.rejected ?? []));
    merged.notInOriginStep.push(...(res.data.conflicts.notInOriginStep ?? []));
    merged.notSubscribed.push(...res.data.conflicts.notSubscribed);
  }
  return merged;
}

/** Reprovação (EU-552): `POST /jobs/:id/reject` em lotes. */
export async function runReject(input: {
  jobId: string;
  originStepId: string | null;
  userIds: string[];
  dryRun: boolean;
}): Promise<BulkClassification> {
  const merged = emptyClassification();
  for (const batch of chunk(input.userIds, BULK_BATCH_SIZE)) {
    const res = await fetchClient<RejectResponse>({
      method: 'POST',
      url: `/jobs/${input.jobId}/reject`,
      data: {
        userIds: batch,
        ...(input.originStepId ? { originStepId: input.originStepId } : {}),
        dryRun: input.dryRun,
      },
    });
    if (res.error) {
      throw new BulkOperationError(
        `Bulk reject failed (status ${res.error.status})`,
        res.error.status
      );
    }
    merged.eligible.push(...res.data.rejected);
    merged.cancelled.push(...res.data.conflicts.cancelled);
    merged.alreadyProcessed.push(...res.data.conflicts.alreadyRejected);
    merged.notSubscribed.push(...res.data.conflicts.notSubscribed);
  }
  return merged;
}

/**
 * RN-05 (EU-551) — "Disparar Fator H imediatamente": push manual best-effort
 * dos movimentados (`POST /jobs/:id/fator-h/push`, lotes de 50 — cap do
 * contrato). Falha NÃO desfaz a movimentação; o caller decide o toast.
 */
export async function pushFatorH(jobId: string, userIds: string[]): Promise<void> {
  for (const batch of chunk(userIds, FATORH_PUSH_BATCH_SIZE)) {
    const res = await fetchClient({
      method: 'POST',
      url: `/jobs/${jobId}/fator-h/push`,
      data: { userIds: batch },
    });
    if (res.error) {
      throw new BulkOperationError(
        `Fator H push failed (status ${res.error.status})`,
        res.error.status
      );
    }
  }
}

export interface BulkPhaseOption {
  id: string;
  name: string;
  type: string | null;
  /** Posição da fase na vaga — base da régua de direção (restrições). */
  position: number;
}

/** Fases da vaga (ordenadas por position) para os selects de origem/destino. */
export function phaseOptionsFromJob(job: JobDetail | undefined): BulkPhaseOption[] {
  const phases = (job?.phases ?? []) as {
    id: string;
    name: string;
    type?: string | null;
    position?: number;
  }[];
  return [...phases]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((p) => ({ id: p.id, name: p.name, type: p.type ?? null, position: p.position ?? 0 }));
}

/** RN-05 — o checkbox do Fator H só aparece quando o destino é a Jornada de aprofundamento. */
export function isDeepDivePhase(option: BulkPhaseOption | undefined): boolean {
  return option?.type === 'jornada-aprofundamento';
}

/**
 * RN "Restrições de fase inicial e final" (2026-07-30) — espelha o guard do
 * Core (`phaseDirectionViolation`): a régua é o TIPO da fase de ORIGEM +
 * a direção POR POSIÇÃO (a ordem das fases é livre no editor):
 * origem Trilha Online não retrocede; origem Contratação não avança.
 * Demais fases: direção livre, inclusive pulando etapas.
 */
export function phaseDirectionError(
  origin: BulkPhaseOption | undefined,
  dest: BulkPhaseOption | undefined
): string | null {
  if (!origin || !dest || origin.id === dest.id) return null;
  if (origin.type === 'trilha-online' && dest.position < origin.position) {
    return 'Não é possível retroceder a partir da fase Trilha Online';
  }
  if (origin.type === 'contratacao-final' && dest.position > origin.position) {
    return 'Não é possível avançar a partir da fase Contratação';
  }
  return null;
}
